import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/auth';
import MerchantHeader from '../components/MerchantHeader';
import {
  MessageSquare, TrendingUp, TrendingDown, AlertTriangle, Star,
  ThumbsUp, ThumbsDown, Minus, Target, ArrowRight, CheckCircle,
  Clock, BarChart3, Lightbulb, DollarSign, Megaphone, Users
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '';

export default function FeedbackEnginePage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const profile = await authService.getProfile();
      setUser(profile.member);
      const brandId = profile.member?.brand_id || profile.member?.merchant_id;
      if (brandId) {
        const res = await fetch(`${API}/api/feedback/dashboard?brand_id=${brandId}`);
        if (res.ok) setDashboard(await res.json());
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to load:', error);
      navigate('/login');
    }
  };

  const handleLogout = async () => {
    await authService.logout();
    sessionStorage.clear();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const nps = dashboard?.nps || {};
  const patterns = dashboard?.patterns || [];
  const actions = dashboard?.open_actions || [];
  const fundingImpact = dashboard?.funding_impact || {};

  const getNPSColor = (score) => {
    if (score >= 50) return 'text-green-600';
    if (score >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getNPSBg = (score) => {
    if (score >= 50) return 'bg-green-50 border-green-200';
    if (score >= 0) return 'bg-yellow-50 border-yellow-200';
    return 'bg-red-50 border-red-200';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <MerchantHeader user={user} onLogout={handleLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MessageSquare className="h-6 w-6 text-indigo-600" />
            Customer Feedback & NPS Engine
          </h1>
          <p className="text-gray-500 mt-1">
            Agentic feedback intelligence — continuous monitoring, pattern detection, and automated action
          </p>
        </div>

        {/* NPS Hero Card */}
        <div className={`rounded-xl border p-6 mb-6 ${getNPSBg(nps.nps_score || 0)}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Net Promoter Score</p>
              <p className={`text-5xl font-bold mt-1 ${getNPSColor(nps.nps_score || 0)}`}>
                {nps.nps_score != null ? Math.round(nps.nps_score) : '—'}
              </p>
              <div className="flex items-center gap-2 mt-2">
                {nps.trend_direction === 'improving' && (
                  <span className="flex items-center text-green-600 text-sm">
                    <TrendingUp className="h-4 w-4 mr-1" /> +{Math.abs(nps.nps_change || 0).toFixed(0)} pts
                  </span>
                )}
                {nps.trend_direction === 'declining' && (
                  <span className="flex items-center text-red-600 text-sm">
                    <TrendingDown className="h-4 w-4 mr-1" /> {(nps.nps_change || 0).toFixed(0)} pts
                  </span>
                )}
                {nps.trend_direction === 'stable' && (
                  <span className="flex items-center text-gray-500 text-sm">
                    <Minus className="h-4 w-4 mr-1" /> Stable
                  </span>
                )}
                <span className="text-gray-400">|</span>
                <span className="text-sm text-gray-600">{nps.response_count || 0} responses</span>
              </div>
            </div>

            <div className="flex gap-6">
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mx-auto">
                  <ThumbsUp className="h-5 w-5 text-green-600" />
                </div>
                <p className="text-lg font-bold text-green-600 mt-1">{nps.promoter_pct || 0}%</p>
                <p className="text-xs text-gray-500">Promoters</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-yellow-100 mx-auto">
                  <Minus className="h-5 w-5 text-yellow-600" />
                </div>
                <p className="text-lg font-bold text-yellow-600 mt-1">{nps.passive_pct || 0}%</p>
                <p className="text-xs text-gray-500">Passives</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mx-auto">
                  <ThumbsDown className="h-5 w-5 text-red-600" />
                </div>
                <p className="text-lg font-bold text-red-600 mt-1">{nps.detractor_pct || 0}%</p>
                <p className="text-xs text-gray-500">Detractors</p>
              </div>
            </div>
          </div>
        </div>

        {/* Funding Impact Card */}
        {fundingImpact.rate_adjustment_bps != null && fundingImpact.rate_adjustment_bps !== 0 && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-6">
            <div className="flex items-center gap-3">
              <DollarSign className="h-5 w-5 text-indigo-600" />
              <div>
                <p className="font-medium text-indigo-900">
                  NPS Funding Impact: {fundingImpact.rate_adjustment_bps < 0 ? '' : '+'}
                  {(fundingImpact.rate_adjustment_bps / 100).toFixed(2)}% rate adjustment
                </p>
                <p className="text-sm text-indigo-700 mt-0.5">{fundingImpact.rationale}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg mb-6 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'patterns', label: 'Detected Patterns', icon: AlertTriangle },
            { id: 'actions', label: 'Action Items', icon: Target },
            { id: 'feedback', label: 'Recent Feedback', icon: MessageSquare },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-white text-indigo-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Active Surveys */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Megaphone className="h-5 w-5 text-indigo-600" />
                Active Surveys
              </h3>
              {(dashboard?.active_surveys || []).length === 0 ? (
                <div className="text-center py-8 text-gray-400">
                  <Megaphone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No active surveys</p>
                  <p className="text-sm mt-1">Set up automated NPS surveys to start collecting feedback</p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {(dashboard.active_surveys || []).map((survey, i) => (
                    <li key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">{survey.name}</p>
                        <p className="text-xs text-gray-500">Trigger: {survey.trigger}</p>
                      </div>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Active</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* How Feedback Powers Your Business */}
            <div className="bg-white rounded-xl border p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-amber-500" />
                The Feedback Flywheel
              </h3>
              <div className="space-y-3">
                {[
                  { step: 'Collect', desc: 'Multi-channel feedback from surveys, reviews, social', icon: MessageSquare, color: 'text-blue-600' },
                  { step: 'Analyze', desc: 'AI detects patterns, sentiment shifts, and urgency', icon: BarChart3, color: 'text-purple-600' },
                  { step: 'Act', desc: 'Auto-generated action items with root cause analysis', icon: Target, color: 'text-orange-600' },
                  { step: 'Measure', desc: 'Track if actions improved NPS and revenue', icon: TrendingUp, color: 'text-green-600' },
                  { step: 'Fund', desc: 'Strong NPS = lower lending rates', icon: DollarSign, color: 'text-indigo-600' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className={`mt-0.5 ${item.color}`}>
                      <item.icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-gray-900">{item.step}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'patterns' && (
          <div className="space-y-4">
            {patterns.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                <AlertTriangle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No patterns detected yet</p>
                <p className="text-sm mt-1">The agent continuously scans for sentiment shifts, emerging issues, and product opportunities</p>
              </div>
            ) : (
              patterns.map((pattern, i) => (
                <div key={i} className={`bg-white rounded-xl border p-4 ${
                  pattern.priority === 'critical' ? 'border-red-300 bg-red-50' :
                  pattern.priority === 'high' ? 'border-orange-300 bg-orange-50' :
                  'border-gray-200'
                }`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                        pattern.priority === 'critical' ? 'bg-red-100 text-red-700' :
                        pattern.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        {pattern.type?.replace('_', ' ').toUpperCase()}
                      </span>
                      <p className="font-medium text-gray-900 mt-2">{pattern.description}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs font-bold ${
                      pattern.priority === 'critical' ? 'bg-red-200 text-red-800' :
                      pattern.priority === 'high' ? 'bg-orange-200 text-orange-800' :
                      'bg-gray-200 text-gray-700'
                    }`}>{pattern.priority?.toUpperCase()}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'actions' && (
          <div className="space-y-3">
            {actions.length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                <CheckCircle className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No open action items</p>
                <p className="text-sm mt-1">When the agent detects issues, it creates action items here</p>
              </div>
            ) : (
              actions.map((action, i) => (
                <div key={i} className="bg-white rounded-xl border p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-gray-900">{action.title}</p>
                      <p className="text-sm text-gray-500 mt-1">{action.recommended_action}</p>
                    </div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      action.status === 'action_generated' ? 'bg-yellow-100 text-yellow-700' :
                      action.status === 'assigned' ? 'bg-blue-100 text-blue-700' :
                      action.status === 'in_progress' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>{action.status?.replace('_', ' ')}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'feedback' && (
          <div className="space-y-3">
            {(dashboard?.recent_feedback || []).length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center text-gray-400">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No feedback collected yet</p>
                <p className="text-sm mt-1">Connect feedback channels to start collecting customer insights</p>
              </div>
            ) : (
              (dashboard.recent_feedback || []).map((fb, i) => (
                <div key={i} className="bg-white rounded-xl border p-4">
                  <div className="flex items-start gap-3">
                    <div className={`mt-1 ${
                      fb.sentiment === 'very_positive' || fb.sentiment === 'positive' ? 'text-green-500' :
                      fb.sentiment === 'very_negative' || fb.sentiment === 'negative' ? 'text-red-500' :
                      'text-gray-400'
                    }`}>
                      {fb.sentiment?.includes('positive') ? <ThumbsUp className="h-4 w-4" /> :
                       fb.sentiment?.includes('negative') ? <ThumbsDown className="h-4 w-4" /> :
                       <Minus className="h-4 w-4" />}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">{fb.raw_text?.substring(0, 200)}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-400">{fb.channel}</span>
                        {fb.nps_score != null && (
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            fb.nps_score >= 9 ? 'bg-green-100 text-green-700' :
                            fb.nps_score >= 7 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>NPS: {fb.nps_score}</span>
                        )}
                        {fb.star_rating && (
                          <span className="text-xs flex items-center gap-0.5">
                            <Star className="h-3 w-3 text-amber-400 fill-amber-400" />
                            {fb.star_rating}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
}
